import { canManageStaffRequisitions, canOpenMyStaffRequisitions, useStaffRequisitionAccess } from "@/features/staff-requisitions/access";
import type { Capabilities as StaffRequisitionAuthority } from "@/features/staff-requisitions/api";
import { useEmployeeFeedbackAuthority, canOpenMyEmployeeFeedback, canReviewEmployeeFeedback, canManageEmployeeFeedback, canReadEmployeeFeedbackIdentity } from "@/features/employee-feedback/authority";
import type { Authority as EmployeeFeedbackAuthority } from "@/features/employee-feedback/api";
import { canOpenVehicleRequestManagement, useVehicleRequestAccess } from "@/features/vehicle-requests/use-vehicle-request-access";
import { canOpenDocumentRequestManagement, useDocumentRequestAccess } from "@/features/document-requests/use-document-request-access";
import { canOpenCafeteriaManagement, canOpenMyCafeteria, useCafeteriaAuthority } from "@/features/cafeteria/authority";
import type { Authority as CafeteriaAuthority } from "@/features/cafeteria/api";
import { canOpenMyPolicies, canOpenPolicyManagement, usePolicyAuthority, type PolicyAuthority } from "@/features/policies/authority";
// The ONE computation that answers "which menu entries may this caller see?".
//
// It was inline in LeftRail until the UI tour needed the same answer. Two
// consumers with two copies of a permission predicate is the defect class this
// repo has already paid for (copied permission lists drift SHUT — the client
// set silently lags the server's). So the predicate moved here and LeftRail
// now consumes it; the tour derives its step list from `visibleKeys` and can
// never advertise a page the rail hides.
//
// The signals (ADR-014 §1 / ADR-017 — advisory only, the server gates every
// route):
//   * per-item `requiredCapability` / `requiredTier` → effective permissions
//     + server-resolved scope tier;
//   * per-section `managementOnly` → scope tier ≠ employee_self, because the
//     read cells those screens gate on are held by EVERY employee for their
//     own rows;
//   * per-item `requiredAccess` → the destination page's OWN predicate, for
//     pages that open on an exact grant or a JWT role that no matrix cell
//     shows (learning authoring, survey admin, the support-agent inbox). Each
//     is answered by the function the page itself imports, so the entry and the
//     page cannot disagree (E23: the menu offered links the page then refused).
//
// Default-CLOSED while authority is in flight: an unresolved tier ranks below
// every requirement and an unresolved capability set is empty. (The rail used
// to default-OPEN "so it doesn't flash-hide" — written when the admin menu only
// mounted on admin routes. With one rail for every user, default-open would
// flash management entries at every plain employee on every load; an entitled
// admin seeing them slightly late is the lesser harm — 76501d16.) `resolved` is
// the separate signal for callers that must not act on a half-answer — the
// rail is happy to render progressively, but a tour panel opened mid-flight
// would introduce the user to a SUBSET of their product and then never
// re-open (seen_version is written once).

import { scopeTierRank, useScopeTierState, type ScopeTier } from "@/api/use-scope-tier";
// Deep import (NOT the feature barrel): the barrel re-exports
// RolesPermissionsContent, and a static barrel import from a module the eager
// shell pulls in would drag the whole roles-permissions screen into the main
// chunk, defeating the router's lazy split for /settings/roles.
import {
  capabilityMatches,
  useEffectivePermissions,
  type Capability,
} from "@/features/roles-permissions/use-effective-permissions";
// Leaf modules, not the feature barrels, for the same chunking reason. Each is
// the module the destination page imports its own predicate from.
import { canOpenLearningManagement, useLearningAuthority, type Authority } from "@/features/learning/api";
import { canReadEeResults, useMeIdentity } from "@/features/surveys/use-me-identity";
import { canOpenFormManagement, useFormAuthority, type FormAuthority } from "@/features/forms/authority";
import { canOpenExpenseManagement, useExpenseAccess } from "@/features/expenses/use-expense-access";
import { isSupportAgent } from "@/features/support-tickets/use-support-identity";
import { useOneOnOneAccess } from "./use-one-on-one-access";

import {
  MENU_SECTIONS,
  menuLinks,
  type MenuAccessGate,
  type MenuItem,
  type MenuSection,
} from "./menu-config";

export interface MenuAuthority {
  readonly capabilities: ReadonlySet<Capability>;
  readonly scopeTier: ScopeTier | undefined;
  /** The page-level gates this caller clears (`MenuItem.requiredAccess`). */
  readonly access: ReadonlySet<MenuAccessGate>;
}

/**
 * The page-level gates a caller clears, from the same predicates the pages use.
 * `learningAuthority` is undefined until it resolves (or when it was never
 * asked), and undefined clears nothing: default-closed like every other axis.
 */
export function resolveMenuAccess(input: {
  readonly roles: readonly string[];
  readonly learningAuthority: Authority | undefined;
  readonly formAuthority?: FormAuthority;
  readonly policyAuthority?: PolicyAuthority;
  readonly cafeteriaAuthority?: CafeteriaAuthority;
  readonly employeeFeedbackAuthority?: EmployeeFeedbackAuthority;
  readonly staffRequisitionAuthority?: StaffRequisitionAuthority;
  readonly vehicleRequestAuthority?: { readonly can_manage: boolean };
  readonly documentRequestAuthority?: { readonly can_manage: boolean };
  readonly expenseAuthority?: { can_manage: boolean };
  readonly oneOnOneAccess?: { readonly granted: boolean };
}): ReadonlySet<MenuAccessGate> {
  const gates = new Set<MenuAccessGate>();
  if (canOpenMyEmployeeFeedback(input.employeeFeedbackAuthority)) gates.add("feedback_self");
  if (canOpenMyStaffRequisitions(input.staffRequisitionAuthority)) gates.add("staff_requisitions_self");
  if (canManageStaffRequisitions(input.staffRequisitionAuthority)) gates.add("staff_requisitions_management");
  if (canReviewEmployeeFeedback(input.employeeFeedbackAuthority)) gates.add("feedback_review");
  if (canManageEmployeeFeedback(input.employeeFeedbackAuthority)) gates.add("feedback_manage");
  if (canReadEmployeeFeedbackIdentity(input.employeeFeedbackAuthority)) gates.add("feedback_identity");
  if (canOpenLearningManagement(input.learningAuthority)) gates.add("learning_management");
  if (canReadEeResults(input.roles)) gates.add("survey_management");
  if (isSupportAgent(input.roles)) gates.add("support_agent");
  if (canOpenFormManagement(input.formAuthority)) gates.add("forms_management");
  if (canOpenExpenseManagement(input.expenseAuthority)) gates.add("expenses_management");
  if (canOpenPolicyManagement(input.policyAuthority)) gates.add("policies_management");
  if (canOpenMyPolicies(input.policyAuthority)) gates.add("policies_self");
  if (canOpenVehicleRequestManagement(input.vehicleRequestAuthority)) gates.add("vehicle_requests_management");
  if (canOpenDocumentRequestManagement(input.documentRequestAuthority)) gates.add("document_requests_management");
  if (canOpenCafeteriaManagement(input.cafeteriaAuthority)) gates.add("cafeteria_management");
  if (canOpenMyCafeteria(input.cafeteriaAuthority)) gates.add("cafeteria_self");
  if (input.oneOnOneAccess?.granted === true) gates.add("one_on_ones_self");
  return gates;
}

/**
 * Filter the nav manifest down to what `authority` may see: sections whose
 * `managementOnly` gate clears, carrying only the items whose capability and
 * tier gates clear. Sections left with no visible item are dropped.
 *
 * Pure — section ORDER is the manifest's own; each consumer applies
 * `orderSectionsByAuthority` (menu-config) on top. Keeping the sort out of the
 * filter lets a surface order as it needs without a second visibility rule.
 */
export function filterMenuByAuthority(
  sections: readonly MenuSection[],
  authority: MenuAuthority,
): readonly MenuSection[] {
  const tierRank = scopeTierRank(authority.scopeTier);
  const hasManagementScope =
    authority.scopeTier !== undefined && authority.scopeTier !== "employee_self";

  const itemVisible = (item: MenuItem): boolean =>
    (item.requiredCapability === undefined ||
      (typeof item.requiredCapability === "string" ? [item.requiredCapability] : item.requiredCapability)
        .every((capability) => capabilityMatches(authority.capabilities, capability))) &&
    (item.requiredTier === undefined ||
      tierRank >= scopeTierRank(item.requiredTier)) &&
    (item.requiredAccess === undefined || authority.access.has(item.requiredAccess));

  // A child is reachable only through its parent, so a hidden parent hides its
  // children; a heading-only parent with no visible child is an empty fold.
  const visibleItem = (item: MenuItem): MenuItem | null => {
    if (!itemVisible(item)) return null;
    if (item.children === undefined) return item;
    const children = item.children.filter(itemVisible);
    if (item.href === undefined) return children.length === 0 ? null : { ...item, children };
    return { ...item, children };
  };

  const out: MenuSection[] = [];
  for (const section of sections) {
    if (section.managementOnly === true && !hasManagementScope) continue;
    const items = section.items.map(visibleItem).filter((item): item is MenuItem => item !== null);
    if (items.length === 0) continue;
    out.push({ ...section, items });
  }
  return out;
}

export interface VisibleMenu {
  /** Manifest-ordered visible sections, each carrying only visible items. */
  readonly sections: readonly MenuSection[];
  /** Manifest-ordered visible item keys — the tour's step selector. */
  readonly visibleKeys: readonly string[];
  /**
   * Every authority signal has landed. False while one is in flight or the
   * tier / capability query has FAILED; a consumer that must not show a
   * half-answer waits on this.
   */
  readonly resolved: boolean;
  /**
   * The authority behind the answer could not be READ (tier or capability
   * cells). A route guard needs this apart from `resolved`: "still asking"
   * renders a loading state, "could not ask" an outage notice, and neither is
   * the denial that "asked, and the answer is no" gets.
   */
  readonly failed: boolean;
}

/** React binding over {@link filterMenuByAuthority} for the live caller. */
export function useVisibleMenu(): VisibleMenu {
  const { capabilities, isLoading, isError } = useEffectivePermissions();
  const { tier: scopeTier, isError: tierFailed } = useScopeTierState();
  const identity = useMeIdentity();
  // Only a caller who can reach the management group has a learning entry to
  // decide, so plain employees never pay for the authority call.
  const asksLearning = scopeTierRank(scopeTier) > scopeTierRank("employee_self");
  const learning = useLearningAuthority({ enabled: asksLearning, staleTime: 60_000 });

  const asksForms = capabilities.has("forms.read");
  const forms = useFormAuthority({ enabled: asksForms });
  const asksStaffRequisitions = capabilities.has("staff_requisitions.read");
  const staffRequisitions = useStaffRequisitionAccess({ enabled: asksStaffRequisitions });
  const asksFeedback = capabilities.has("employee_feedback.read");
  const feedback = useEmployeeFeedbackAuthority({ enabled: asksFeedback });
  const asksVehicles = capabilities.has("vehicle_requests.read");
  const vehicles = useVehicleRequestAccess({ enabled: asksVehicles });
  const asksDocuments = capabilities.has("document_requests.read");
  const documents = useDocumentRequestAccess({ enabled: asksDocuments });
  const asksCafeteria = capabilities.has("cafeteria.read");
  const cafeteria = useCafeteriaAuthority({ enabled: asksCafeteria });
  const asksPolicies = capabilities.has("policy.read");
  const policies = usePolicyAuthority({ enabled: asksPolicies });
  const asksExpenses = capabilities.has("expenses.read");
  const expenses = useExpenseAccess({ enabled: asksExpenses });
  const asksOneOnOnes = capabilities.has("performance.read") && identity.employeeId !== null;
  const oneOnOnes = useOneOnOneAccess(identity.employeeId, { enabled: asksOneOnOnes });
  const access = resolveMenuAccess({ ...(feedback.data ? { employeeFeedbackAuthority: feedback.data } : {}), ...(staffRequisitions.data ? { staffRequisitionAuthority: staffRequisitions.data } : {}), roles: identity.roles, learningAuthority: learning.data, ...(vehicles.data ? { vehicleRequestAuthority: vehicles.data } : {}), ...(documents.data ? { documentRequestAuthority: documents.data } : {}), ...(cafeteria.data ? { cafeteriaAuthority: cafeteria.data } : {}), ...(policies.data ? { policyAuthority: policies.data } : {}), ...(forms.data ? { formAuthority: forms.data } : {}), ...(expenses.data ? { expenseAuthority: expenses.data } : {}), ...(oneOnOnes.data ? { oneOnOneAccess: oneOnOnes.data } : {}) });
  const sections = filterMenuByAuthority(MENU_SECTIONS, { capabilities, scopeTier, access });
  const visibleKeys = sections.flatMap((section) => menuLinks(section.items).map((item) => item.key));

  // `isError` is load-bearing, not belt-and-braces: a FAILED effective-permissions
  // query leaves isLoading false with an EMPTY capability set, which is
  // indistinguishable from "this caller holds nothing". The rail can live with
  // that (it re-renders when the retry lands), but the tour cannot — it opens
  // once and writes seen_version, so a 503 on that one call would introduce a
  // director to the 20-step employee product permanently. The scope-tier axis
  // already fails closed this way (useScopeTier returns undefined on error);
  // this makes the capability axis match. Measured 2026-08-04: without it the
  // panel opened at "Step 1 of 20" for an executive_broad caller.
  //
  // The page-level gates join the wait for the same reason: a tour opened
  // before the learning authority lands would omit "Eğitim yönetimi" from an
  // author's tour for good. A FAILED authority call settles (`isPending` is
  // false on error) and simply leaves that one entry hidden, which is the
  // default-closed answer; it must not hold the tour hostage to an unrelated
  // endpoint.
  const resolved =
    !isLoading &&
    !isError &&
    scopeTier !== undefined &&
    !identity.isLoading &&
    !(asksLearning && learning.isPending) &&
    !(asksForms && forms.isPending) &&
    !(asksExpenses && expenses.isPending) &&
    !(asksPolicies && policies.isPending) &&
    !(asksFeedback && feedback.isPending) &&
    !(asksStaffRequisitions && staffRequisitions.isPending) &&
    !(asksVehicles && vehicles.isPending) &&
    !(asksDocuments && documents.isPending) &&
    !(asksCafeteria && cafeteria.isPending) &&
    !(asksOneOnOnes && oneOnOnes.isPending);

  return { sections, visibleKeys, resolved, failed: isError || tierFailed };
}
